import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { Oval } from "react-loader-spinner";
import Modal from "components/modal";
import Input from "components/input";
import Select from "components/selectWithComponents";
import { ReactComponent as AddCircleIcon } from "images/auth/add-circle.svg";
import { ReactComponent as TrashIcon } from "images/auth/trash.svg";
import { isValidEmail, validateEmail } from "utils/helper";
import { LANGUAGE_MODEL_NONE, LANGUAGE_MODEL_PAIR } from "utils/constantValues";

function UpsertNewMemberModal({
  title = "",
  isOpen,
  onClose = () => {},
  onSubmit = () => {},
  isLoading = false,
  isEditModal = false,
  defaultValues = {
    firstName: "",
    lastName: "",
    email: "",
    //  "serviceId-0":"33740a6d-a92f-4000-b4be-481b4a30c848"
    //  "domainId-0":"33740a6d-a92f-4000-b4be-481b4a30c848"
    userRatesId: {}, // serviceId:[userRateId]
    userDomains: {}, // domainId:[subDomainId]
  },
  userRates = [],
  userServices = [],
  userDomains = [],
  userSubDomains = [],
}) {
  const [serviceSection, setServiceSection] = useState(
    Object.keys(defaultValues.userRatesId).length > 0
      ? userServices.filter((elm) =>
          Object.keys(defaultValues.userRatesId).includes(elm.id)
        )
      : userServices[0]
      ? [userServices[0]]
      : []
  );

  const [domainsSection, setDomainSection] = useState(() => {
    return Object.keys(defaultValues.userDomains).length > 0
      ? userDomains.filter((elm) =>
          Object.keys(defaultValues.userDomains).includes(elm.id)
        )
      : userDomains[0]
      ? [userDomains[0]]
      : [];
  });

  const addAnotherService = (index) => {
    if (!userServices[index]) {
      return;
    }
    setServiceSection((prevState) => [...prevState, userServices[index]]);
  };

  const addAnotherDomain = (index) => {
    if (!userDomains[index]) {
      return;
    }
    setDomainSection((prevState) => [...prevState, userDomains[index]]);
  };

  const servicesOptions = userServices.map((service) => {
    return {
      ...service,
      value: service.id,
      label: service.title,
    };
  });

  const domainsOptions = userDomains.map((domain) => {
    return {
      ...domain,
      value: domain.id,
      label: domain.title,
    };
  });

  const [t] = useTranslation();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    unregister,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: defaultValues,
    reValidateMode: "all",
  });

  useEffect(() => {
    return () => {
      reset();
    };
  }, [isOpen]);
  return (
    <Modal isOpen={isOpen}>
      <form
        className="flex p-[32px] flex-col  gap-[36px] w-[80%]  max-w-[954px] max-h-[80%] overflow-auto bg-white rounded-[18px] relative"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div
          className="grid overflow-auto  h-full pb-[20px]"
          style={{ maxHeight: `calc(100% - 110px)` }}
        >
          <div className="text-base-black font-bold text-[30px] not-italic">
            {title}
          </div>

          {/* ***************** PERSONAL INFORMATION SECTION **********************  */}
          <div className="flex flex-col gap-[24px] self-stretch">
            <div className="test-base-black font-bold text-[20px]">
              {t("personalInformation")}
            </div>
            <div className="grid md-max:grid-cols-1 grid-cols-3 items-center gap-[16px] self-stretch">
              <Input
                // value={watch("firstName") || ""}
                label={t("firstName")}
                isRequired={false}
                placeholder={t("placeholderAgency", {
                  title: t("firstName").toLowerCase(),
                })}
                name="firstName"
                register={register}
                registerArgs={{
                  required: false,
                }}
                containerClassName="flex flex-col sm-max:grid sm-max:w-full gap-[10px]"
                labelClassName="text-base-black font-semibold text-[16px]"
                inputClassName={`flex h-[43px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${"border-default-color"} bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
              />

              <Input
                // value={watch("lastName") || ""}
                label={t("lastName")}
                isRequired={false}
                placeholder={t("placeholderAgency", {
                  title: t("lastName").toLowerCase(),
                })}
                name="lastName"
                register={register}
                registerArgs={{
                  required: false,
                }}
                containerClassName="flex flex-col sm-max:grid sm-max:w-full gap-[10px]"
                labelClassName="text-base-black font-semibold text-[16px]"
                inputClassName={`flex h-[43px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${"border-default-color"} bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
              />

              {!isEditModal && (
                <Input
                  label={t("emailTitle")}
                  isRequired={true}
                  type="email"
                  name="email"
                  // value={watch("email") || ""}
                  placeholder={t("placeHolderType", {
                    title: t("emailTitle").toLowerCase(),
                  })}
                  errorMessage={
                    errors.email && !isValidEmail(watch("email"))
                      ? watch("email") && !isValidEmail(watch("email"))
                        ? t("invalidEmail")
                        : t("errorField", { field: t("emailTitle") })
                      : ""
                  }
                  register={register}
                  registerArgs={{
                    required: t("errorField", { field: t("emailTitle") }),
                    validate: validateEmail,
                  }}
                  containerClassName="flex flex-col sm-max:grid sm-max:w-full gap-[10px]"
                  labelClassName="text-base-black font-semibold text-[16px]"
                  inputClassName={`flex h-[43px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
                    errors.email && !isValidEmail(watch("email"))
                      ? "border-red"
                      : "border-default-color"
                  } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
                />
              )}
            </div>
          </div>

          {/* ***************** Service SECTION **********************  */}
          <div className="flex flex-col gap-[24px] self-stretch mt-[10px]">
            <div className="test-base-black font-bold text-[20px]">
              {t("services")}
            </div>

            <div className="flex flex-col gap-[20px] self-stretch">
              {serviceSection.map((_, index) => {
                let serviceId = watch(`serviceId-${index}`);
                let userRatesId = userRates.reduce((acc, serviceRate) => {
                  if (serviceId === serviceRate?.serviceId) {
                    let obj = {
                      serviceRateId: serviceRate.rateId,
                      fromLanguageId: serviceRate?.fromLang?.id,
                      toLanguageId: serviceRate?.toLang?.id,
                      serviceId,
                      languageTitle:
                        serviceRate.languageModel === LANGUAGE_MODEL_NONE
                          ? ""
                          : serviceRate.languageModel === LANGUAGE_MODEL_PAIR
                          ? `${serviceRate?.fromLang?.title} to ${serviceRate?.toLang?.title}`
                          : serviceRate?.toLang?.title,
                      isDisabled:
                        serviceRate.languageModel === LANGUAGE_MODEL_NONE,
                    };
                    acc.push(obj);
                  }

                  return acc;
                }, []);

                const serviceRateOptions = userRatesId
                  .filter((el) => !el.isDisabled)
                  .map((el) => {
                    return {
                      ...el,
                      label: el.languageTitle,
                      value: el.serviceRateId,
                    };
                  });

                let userServiceRates =
                  (watch("userRatesId") && watch("userRatesId")[serviceId]) ||
                  [];

                let values = getValues();

                let chosenServiceIds = Object.keys(values).reduce(
                  (acc, elem) => {
                    if (elem.includes("serviceId-") && values[elem]) {
                      acc.push(values[elem]);
                    }
                    return acc;
                  },
                  []
                );

                let isRequired = serviceRateOptions.length > 0;
                return (
                  <div
                    className="grid  md-max:grid-cols-1 grid-cols-3 items-center gap-[16px] self-stretch"
                    key={`${index}`}
                  >
                    <Select
                      label={t("Service")}
                      placeholder={t("placeholderSelect", {
                        field: t("Service").toLowerCase(),
                      })}
                      name={`serviceId-${index}`}
                      containerClassName="flex flex-1 flex-col gap-[10px] min-w-[286px] max-w-[286px] sm-max:w-full"
                      value={
                        watch(`serviceId-${index}`)
                          ? servicesOptions.find(
                              (elem) =>
                                elem.value === watch(`serviceId-${index}`)
                            )
                          : null
                      }
                      onChange={(e) => {
                        let data = watch("userRatesId") || {};
                        if (data[serviceId]) {
                          delete data[serviceId];
                        }

                        setValue(`serviceId-${index}`, e.value);
                        setValue(`lang-service-${index}`, "");

                        let service = userRates.reduce((acc, serviceRate) => {
                          if (e.value === serviceRate?.serviceId) {
                            let obj = {
                              serviceRateId: serviceRate.rateId,
                              fromLanguageId: serviceRate?.fromLang?.id,
                              toLanguageId: serviceRate?.toLang?.id,
                              serviceId,
                              languageTitle:
                                serviceRate.languageModel ===
                                LANGUAGE_MODEL_NONE
                                  ? ""
                                  : serviceRate.languageModel ===
                                    LANGUAGE_MODEL_PAIR
                                  ? `${serviceRate?.fromLang?.title} to ${serviceRate?.toLang?.title}`
                                  : serviceRate?.toLang?.title,
                              isDisabled:
                                serviceRate.languageModel ===
                                LANGUAGE_MODEL_NONE,
                            };
                            acc.push(obj);
                          }

                          return acc;
                        }, []);
                        const hasLanguagePair = service
                          .filter((el) => !el.isDisabled)
                          .map((el) => {
                            return {
                              ...el,
                              label: el.languageTitle,
                              value: el.serviceRateId,
                            };
                          });
                        if (hasLanguagePair.length <= 0) {
                          setValue("userRatesId", {
                            ...data,
                            [e.value]: userRates.reduce((acc, serviceRate) => {
                              if (
                                e.value === serviceRate?.serviceId &&
                                serviceRate.languageModel ===
                                  LANGUAGE_MODEL_NONE
                              ) {
                                acc.push(serviceRate.rateId);
                              }

                              return acc;
                            }, []),
                          });
                        } else {
                          setValue("userRatesId", {
                            ...data,
                            [e.value]: {},
                          });
                        }
                      }}
                      closeMenuOnSelect={true}
                      isRequired={true}
                      register={register}
                      registerArgs={{
                        required: t("errorField", { field: t("Service") }),
                      }}
                      errorMessage={
                        errors[`serviceId-${index}`]
                          ? errors[`serviceId-${index}`].message
                          : ""
                      }
                      options={servicesOptions.filter(
                        (el) => !chosenServiceIds.includes(el.value)
                      )}
                    />

                    <Select
                      label={t("languagePairs")}
                      placeholder={t("placeholderSelect", {
                        field: t("languagePairs").toLowerCase(),
                      })}
                      closeMenuOnSelect={true}
                      isMulti={true}
                      withCheckBox
                      isRequired={isRequired}
                      registerArgs={{
                        required: isRequired
                          ? t("errorField", { field: t("languagePairs") })
                          : false,
                      }}
                      errorMessage={
                        errors[`lang-service-${index}`]
                          ? errors[`lang-service-${index}`].message
                          : ""
                      }
                      showError={isRequired && !values[`lang-service-${index}`]}
                      name={`lang-service-${index}`}
                      isDisabled={serviceRateOptions.length <= 0}
                      containerClassName="flex flex-1 flex-col gap-[10px] min-w-[286px] max-w-[286px] sm-max:w-full"
                      value={
                        userServiceRates.length
                          ? serviceRateOptions.filter((elem) =>
                              userServiceRates.includes(elem.value)
                            )
                          : null
                      }
                      onChange={(e) => {
                        let data = watch("userRatesId") || {};
                        setValue(
                          `lang-service-${index}`,
                          e.length > 0 ? serviceId : ""
                        );
                        setValue("userRatesId", {
                          ...data,
                          [serviceId]: e.map((e) => e.value),
                        });
                      }}
                      register={register}
                      options={serviceRateOptions}
                    />
                    {index >= 1 ? (
                      <div className="grid items-center">
                        <div
                          className="flex gap-[8px] cursor-pointer mt-[30px]"
                          onClick={() => {
                            unregister(`serviceId-${index}`);
                            unregister(`lang-service-${index}`);
                            if (
                              values.userRatesId[values[`serviceId-${index}`]]
                            ) {
                              delete values.userRatesId[
                                values[`serviceId-${index}`]
                              ];
                              setValue("userRatesId", values.userRatesId);
                            }

                            let removeServiceSection = serviceSection.filter(
                              (_, i) => i !== index
                            );

                            setServiceSection(removeServiceSection);
                          }}
                        >
                          <TrashIcon />
                        </div>
                      </div>
                    ) : (
                      <div></div>
                    )}
                    {index === serviceSection.length - 1 &&
                      serviceSection.length < servicesOptions.length && (
                        <div className="grid">
                          <div
                            className={`flex gap-[8px] ${"cursor-pointer"}`}
                            onClick={() => {
                              addAnotherService(index);
                            }}
                          >
                            <AddCircleIcon />
                            <div
                              className={`${"text-primary font-bold text-[16px] not-italic"}`}
                            >
                              {t("addAnotherService")}
                            </div>
                          </div>
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ***************** Domains SECTION **********************  */}
          <div className="flex flex-col gap-[24px] self-stretch">
            <div className="test-base-black font-bold text-[20px]">
              {t("domainsSubdomainTitle")}
            </div>

            <div className="flex flex-col gap-[20px] self-stretch">
              {domainsSection.map((_, index) => {
                let domainId = watch(`domainId-${index}`);
                let subDomains = userSubDomains.reduce((acc, subDomain) => {
                  if (domainId === subDomain?.domainId) {
                    acc.push(subDomain);
                  }

                  return acc;
                }, []);

                const subDomainsOptions = subDomains.map((el) => {
                  return {
                    ...el,
                    label: `${el.domainTitle} - ${el.title}`,
                    value: el.id,
                  };
                });

                let userSubDomain =
                  (watch("userDomains") && watch("userDomains")[domainId]) ||
                  [];

                let values = getValues();

                let chosenDomainIds = Object.keys(values).reduce(
                  (acc, elem) => {
                    if (elem.includes("domainId-") && values[elem]) {
                      acc.push(values[elem]);
                    }
                    return acc;
                  },
                  []
                );

                return (
                  <div
                    className="grid md-max:grid-cols-1 grid-cols-3  items-center gap-[16px] self-stretch"
                    key={`${index}`}
                  >
                    <Select
                      label={t("Domain")}
                      placeholder={t("placeholderSelect", {
                        field: t("Domain").toLowerCase(),
                      })}
                      name={`domainId-${index}`}
                      containerClassName="flex flex-1 flex-col gap-[10px] min-w-[286px] max-w-[286px] sm-max:w-full"
                      value={
                        watch(`domainId-${index}`)
                          ? domainsOptions.find(
                              (elem) =>
                                elem.value === watch(`domainId-${index}`)
                            )
                          : null
                      }
                      onChange={(e) => {
                        let data = watch("userDomains") || {};
                        delete data[domainId];
                        setValue(`domainId-${index}`, e.value);
                        setValue("userDomains", {
                          ...data,
                          [e.value]: {},
                        });
                        setValue(`subdomain-id-${index}`, "");
                      }}
                      closeMenuOnSelect={true}
                      isRequired={true}
                      register={register}
                      registerArgs={{
                        required: t("errorField", { field: t("Domain") }),
                      }}
                      errorMessage={
                        errors[`domainId-${index}`]
                          ? errors[`domainId-${index}`].message
                          : ""
                      }
                      options={domainsOptions.filter(
                        (el) => !chosenDomainIds.includes(el.value)
                      )}
                    />

                    <Select
                      label={t("Subdomain")}
                      placeholder={t("placeholderSelect", {
                        field: t("Subdomain").toLowerCase(),
                      })}
                      closeMenuOnSelect={true}
                      isMulti={true}
                      withCheckBox
                      isRequired={true}
                      registerArgs={{
                        required: t("errorField", {
                          field: t("Subdomain"),
                        }),
                      }}
                      errorMessage={
                        errors[`subdomain-id-${index}`]
                          ? errors[`subdomain-id-${index}`].message
                          : ""
                      }
                      showError={!values[`subdomain-id-${index}`]}
                      name={`subdomain-id-${index}`}
                      containerClassName="flex flex-1 flex-col gap-[10px] min-w-[286px] max-w-[286px] sm-max:w-full"
                      value={
                        userSubDomain.length
                          ? subDomainsOptions.filter((elem) =>
                              userSubDomain.includes(elem.value)
                            )
                          : null
                      }
                      onChange={(e) => {
                        let data = watch("userDomains") || {};
                        setValue(
                          `subdomain-id-${index}`,
                          e.length > 0 ? domainId : ""
                        );
                        setValue("userDomains", {
                          ...data,
                          [domainId]: e.map((e) => e.value),
                        });
                      }}
                      isDisabled={subDomainsOptions.length <= 0}
                      register={register}
                      options={subDomainsOptions}
                    />
                    {index >= 1 ? (
                      <div className="grid items-center">
                        <div
                          className="flex gap-[8px] cursor-pointer mt-[30px]"
                          onClick={() => {
                            unregister(`domainId-${index}`);
                            unregister(`subdomain-id-${index}`);
                            if (
                              values.userDomains[values[`domainId-${index}`]]
                            ) {
                              delete values.userDomains[
                                values[`domainId-${index}`]
                              ];
                              setValue("userDomains", values.userDomains);
                            }

                            let removeDomainSection = domainsSection.filter(
                              (_, i) => i !== index
                            );

                            setDomainSection(removeDomainSection);
                          }}
                        >
                          <TrashIcon />
                        </div>
                      </div>
                    ) : (
                      <div></div>
                    )}
                    {index === domainsSection.length - 1 &&
                      domainsSection.length < domainsOptions.length && (
                        <div className="grid">
                          <div
                            className={`flex gap-[8px] ${"cursor-pointer"}`}
                            onClick={() => {
                              addAnotherDomain(index);
                            }}
                          >
                            <AddCircleIcon />
                            <div
                              className={`${"text-primary font-bold text-[16px] not-italic"}`}
                            >
                              {t("addAnotherDomain")}
                            </div>
                          </div>
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-[12px] self-stretch   h-[110px] items-center bg-white w-full border-t-[1px] border-solid border-default-color">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-default-color bg-white h-[46px]"
          >
            <div className="text-base-black font-bold text-[16px] not-italic">
              {t("Cancel")}
            </div>
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-primary bg-primary h-[46px]"
          >
            <Oval
              height={30}
              width={30}
              color="#FFF"
              visible={isLoading}
              ariaLabel="oval-loading"
              secondaryColor="#FFF"
              strokeWidth={5}
              strokeWidthSecondary={3}
            />
            <div className="text-white font-bold text-[16px] not-italic">
              {t("Save")}
            </div>
          </button>
        </div>
      </form>
    </Modal>
  );
}

UpsertNewMemberModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
  isEditModal: PropTypes.bool,
  defaultValues: PropTypes.object,
  userRates: PropTypes.array,
};

export default UpsertNewMemberModal;
